// Settings -> Team (SPEC §19, 2026-10-01): invite a teammate by email (first name, last name, email, role) and track
// each invite: status, the three onboarding emails (invite, welcome, first week) with any send error, Resend (a new
// link; the old one stops working) and Revoke. The user list below it (UsersAdmin) is unchanged.
import { useState } from 'preact/hooks';
import type { Role } from '../../../src/shared/api';
import type { InviteCreateRequest, TeamInvite } from '../../../src/shared/api-team';
import { api, errorMessage } from '../api';
import { ErrorBanner, fmtDateTime, toast } from '../components/ui';
import { ConfirmModal, Field, inputValue, useLoad } from './ui';

const emailOk = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

const STATUS: Record<TeamInvite['status'], { cls: string; label: string }> = {
  pending: { cls: 'chip chip-warn', label: 'invited' },
  accepted: { cls: 'chip chip-ok', label: 'account created' },
  expired: { cls: 'chip chip-bad', label: 'expired' },
  revoked: { cls: 'chip', label: 'revoked' },
};

function EmailCell(props: { label: string; sentAt: string | null; error: string | null; due?: string }) {
  if (props.error) return <div><span class="chip chip-bad" title={props.error}>{props.label}: failed</span><div class="adm-hint" style="white-space:normal;max-width:260px">{props.error}</div></div>;
  if (props.sentAt) return <div><span class="chip chip-ok" title={props.sentAt}>{props.label}: sent</span> <span class="muted" style="font-size:12px">{fmtDateTime(props.sentAt)}</span></div>;
  return <div><span class="chip" title={props.due}>{props.label}: {props.due ?? 'not yet'}</span></div>;
}

export function TeamInvites() {
  const invites = useLoad(() => api.get<TeamInvite[]>('/api/invites'), []);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('setter');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<TeamInvite | null>(null);
  const [rowBusy, setRowBusy] = useState<number | null>(null);

  const problems: string[] = [];
  if (!firstName.trim()) problems.push('Enter their first name.');
  if (!lastName.trim()) problems.push('Enter their last name.');
  if (!emailOk(email)) problems.push('Enter a valid email address.');

  const replace = (inv: TeamInvite) => {
    const list = invites.data ?? [];
    invites.setData(list.some((x) => x.id === inv.id) ? list.map((x) => (x.id === inv.id ? inv : x)) : [inv, ...list]);
  };

  const send = async (e: Event) => {
    e.preventDefault();
    if (problems.length) { setError(problems[0]); return; }
    setBusy(true); setError(null);
    try {
      const body: InviteCreateRequest = { firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim().toLowerCase(), role };
      const inv = await api.post<TeamInvite>('/api/invites', body);
      replace(inv);
      if (inv.inviteEmailError) toast(`Invite saved, but the email failed: ${inv.inviteEmailError}`, 'bad');
      else toast(`Invite sent to ${inv.email}.`, 'ok');
      setFirstName(''); setLastName(''); setEmail(''); setRole('setter');
      void invites.reload();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  const resend = async (inv: TeamInvite) => {
    setRowBusy(inv.id);
    try {
      const next = await api.post<TeamInvite>(`/api/invites/${inv.id}/resend`);
      replace(next);
      if (next.inviteEmailError) toast(`The email failed again: ${next.inviteEmailError}`, 'bad');
      else toast(`New invite link sent to ${next.email}. The old link no longer works.`, 'ok');
    } catch (err) { toast(errorMessage(err), 'bad'); } finally { setRowBusy(null); }
  };

  const rows = invites.data ?? [];
  return (
    <div class="stack" style="gap:12px">
      <form class="card stack" style="gap:10px" onSubmit={send}>
        <div class="row" style="justify-content:space-between;flex-wrap:wrap">
          <h3 style="margin:0">Invite a teammate</h3>
          <span class="adm-sub">They get a Valistix email with a link to set their own password (valid 7 days), a welcome email once they're in, and a first-week guide a day later.</span>
        </div>
        <div class="adm-grid">
          <Field label="First name"><input value={firstName} maxLength={60} autocomplete="off" onInput={(e) => setFirstName(inputValue(e))} /></Field>
          <Field label="Last name"><input value={lastName} maxLength={60} autocomplete="off" onInput={(e) => setLastName(inputValue(e))} /></Field>
          <Field label="Email"><input type="email" value={email} autocomplete="off" onInput={(e) => setEmail(inputValue(e))} /></Field>
          <Field label="Role" hint={role === 'admin' ? 'Admins manage campaigns, numbers and settings. Their lead-engine spending still needs PJ’s approval.' : 'Setters dial, book meetings and can request lead searches (PJ approves every lead-engine spend).'}>
            <select value={role} onChange={(e) => setRole(inputValue(e) as Role)}><option value="setter">Setter</option><option value="admin">Admin</option></select>
          </Field>
        </div>
        <ErrorBanner error={error} />
        <div class="row" style="justify-content:flex-end">
          <button type="submit" class="btn btn-primary" disabled={busy}>{busy ? 'Sending…' : 'Send invite'}</button>
        </div>
      </form>

      <ErrorBanner error={invites.error} />
      {rows.length ? (
        <div class="adm-table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Invited</th><th>Emails</th><th /></tr></thead>
            <tbody>{rows.map((inv) => (
              <tr class={inv.status === 'revoked' ? 'adm-dim' : ''}>
                <td><b>{inv.firstName} {inv.lastName}</b></td>
                <td>{inv.email}</td>
                <td>{inv.role}</td>
                <td>
                  <span class={STATUS[inv.status].cls}>{STATUS[inv.status].label}</span>
                  <div class="muted" style="font-size:12px">
                    {inv.status === 'accepted' ? fmtDateTime(inv.acceptedAt) : inv.status === 'revoked' ? fmtDateTime(inv.revokedAt) : `link expires ${fmtDateTime(inv.expiresAt)}`}
                  </div>
                </td>
                <td>{fmtDateTime(inv.createdAt)}<div class="muted" style="font-size:12px">by {inv.invitedByName ?? `#${inv.invitedBy}`}{inv.sentCount > 1 ? ` · sent ${inv.sentCount}×` : ''}</div></td>
                <td class="stack" style="gap:4px">
                  <EmailCell label="Invite" sentAt={inv.inviteEmailSentAt} error={inv.inviteEmailError} />
                  {inv.status === 'accepted' ? <EmailCell label="Welcome" sentAt={inv.welcomeEmailSentAt} error={inv.welcomeEmailError} /> : null}
                  {inv.status === 'accepted' ? <EmailCell label="First week" sentAt={inv.firstWeekEmailSentAt} error={inv.firstWeekEmailError} due="about a day after sign-up" /> : null}
                </td>
                <td>
                  {inv.status === 'pending' || inv.status === 'expired' ? (
                    <div class="row" style="gap:4px;flex-wrap:wrap">
                      <button class="btn btn-sm" disabled={rowBusy === inv.id} onClick={() => void resend(inv)}>{rowBusy === inv.id ? 'Sending…' : 'Resend'}</button>
                      <button class="btn btn-sm" disabled={rowBusy === inv.id} onClick={() => setRevoking(inv)}>Revoke</button>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : invites.data ? <div class="muted">No invites yet.</div> : invites.loading ? <div class="muted">Loading invites…</div> : null}

      {revoking ? (
        <ConfirmModal title={`Revoke the invite for ${revoking.firstName}?`} confirmLabel="Revoke invite" danger
          body={<>The link in {revoking.email}'s invite stops working right away. You can invite them again later.</>}
          onClose={() => setRevoking(null)}
          onConfirm={async () => {
            const next = await api.post<TeamInvite>(`/api/invites/${revoking.id}/revoke`);
            replace(next);
            toast(`Invite for ${next.email} revoked.`, 'ok');
          }} />
      ) : null}
    </div>
  );
}
